/**
 * Metadatos SEO (título y descripción) de cada ruta de la web.
 *
 * Decisión de diseño: un mapa central ruta → metadatos, consumido por UN solo
 * componente (`SeoHead`), en lugar de que cada página llame a un hook. Motivos:
 *  - las páginas no se tocan (una página nueva solo añade una fila aquí y sus
 *    textos en `locales/<idioma>/seo.json`);
 *  - las rutas ya se declaran una vez en `App.js` y aquí se relacionan por su
 *    patrón español; el prefijo de idioma se ignora;
 *  - es puro y testeable sin renderizar páginas (GSAP, etc.).
 * Las rutas desconocidas caen en la meta de la portada.
 *
 * Módulo puro: recibe `t` (fijado al espacio `seo` y al idioma) y `lc`
 * (resuelve campos `{ es, en, fr, pt }` de `data/`), sin depender de i18next.
 */

import { matchPath } from 'react-router-dom';
import { stripLanguagePrefix } from './paths';
import { truncateDescription } from './seo';
import { getRecipeBySlug } from '../data/recipesData';
import { getProductById } from '../data/retailProductsData';
import { getHorecaProductById } from '../data/horecaProductsData';
import { getNewsBySlug } from '../data/newsData';

/**
 * @typedef {Object} SeoRoute
 * @property {string} path - Patrón de ruta en español (sin prefijo de idioma).
 * @property {string} key - Clave de `seo.json` (`pages.<key>.title|description`).
 * @property {(params: Object) => ?{name: *, description: *}} [item] - Para
 *   páginas de detalle: devuelve el elemento con los campos (posiblemente
 *   `{ es, en, fr, pt }`) que dan título y descripción.
 */

/** @type {SeoRoute[]} */
export const SEO_ROUTES = [
  { path: '/', key: 'home' },
  { path: '/nosotros', key: 'about' },
  { path: '/sobre-nosotros', key: 'about' },
  { path: '/productos', key: 'products' },
  { path: '/productos/retail', key: 'retail' },
  {
    path: '/productos/retail/:productId',
    key: 'retail',
    item: ({ productId }) => {
      const p = getProductById(productId);
      return p && { name: p.name, description: p.shortDescription || p.description };
    },
  },
  { path: '/productos/horeca', key: 'horeca' },
  {
    path: '/productos/horeca/:productId',
    key: 'horeca',
    item: ({ productId }) => {
      const p = getHorecaProductById(productId);
      return p && { name: p.name, description: p.shortDescription || p.description };
    },
  },
  { path: '/recetas', key: 'recipes' },
  { path: '/envio-receta', key: 'recipes' },
  {
    path: '/recetas/:slug',
    key: 'recipes',
    item: ({ slug }) => {
      const r = getRecipeBySlug(slug);
      return r && { name: r.title, description: r.description || r.introduction };
    },
  },
  { path: '/noticias', key: 'news' },
  {
    path: '/noticias/:slug',
    key: 'news',
    item: ({ slug }) => {
      const n = getNewsBySlug(slug);
      return n && { name: n.title, description: n.summary };
    },
  },
  { path: '/contacto', key: 'contact' },
  { path: '/transparencia', key: 'transparency' },
  { path: '/transparencia/organizativa', key: 'transparencyOrganizational' },
  { path: '/transparencia/normativa', key: 'transparencyRegulations' },
  { path: '/transparencia/acceso', key: 'transparencyAwarded' },
  { path: '/transparencia/economica', key: 'transparencyEconomic' },
  { path: '/transparencia/contratos', key: 'transparencyContracts' },
  { path: '/transparencia/ayudas', key: 'transparencySubsidies' },
  { path: '/transparencia/servicios', key: 'transparencyServices' },
  { path: '/cookies', key: 'cookies' },
  { path: '/privacidad', key: 'privacy' },
  { path: '/aviso', key: 'legal' },
];

const HOME_KEY = 'home';

/**
 * Metadatos de una sección (título y descripción fijos de `seo.json`).
 * @param {string} key
 * @param {Function} t - `t` del espacio `seo` en el idioma de la ruta.
 * @returns {{title: string, description: string}}
 */
const sectionMeta = (key, t) => ({
  title: t(`pages.${key}.title`),
  description: t(`pages.${key}.description`),
});

/**
 * Resuelve título y descripción de una ruta en el idioma indicado.
 *
 * @param {string} pathname - Ruta actual (con o sin prefijo de idioma).
 * @param {{t: Function, lc: Function}} tools - `t` del espacio `seo` fijado al
 *   idioma y `lc` para resolver campos de `data/` en ese idioma.
 * @returns {{title: string, description: string}} Metadatos de la página. Un
 *   detalle sin elemento (slug inexistente) usa los de su sección; una ruta
 *   desconocida, los de la portada.
 */
export const resolveSeoMeta = (pathname, { t, lc }) => {
  const bare = stripLanguagePrefix(pathname.split(/[?#]/)[0]) || '/';
  for (const route of SEO_ROUTES) {
    const match = matchPath({ path: route.path, end: true }, bare);
    if (!match) continue;
    if (route.item) {
      const item = route.item(match.params);
      const name = item && lc(item.name);
      if (name) {
        return {
          title: t('detailTitle', { name }),
          description: truncateDescription(lc(item.description)),
        };
      }
    }
    return sectionMeta(route.key, t);
  }
  return sectionMeta(HOME_KEY, t);
};
